import { formatDateForAPI } from "../date";
import { onStatusUpdate, allowPush, states } from "../constants/requirements";
import { statusValues } from "../constants/advancements";
import { forEach, pathOr, clone, path, find, isNil, isEmpty } from "ramda";

const _findRequirementId = ({ parent = {}, item, id }) => {
  const found = find(
    (requirement) => String(requirement.id) === String(id),
    item
  );

  if (found) {
    return [found];
  }

  for (let i = 0; i < item.length; i += 1) {
    const itemStatus = item[i].status;

    if (item[i].requirements) {
      const child = _findRequirementId({
        item: item[i].requirements,
        parent: item[i],
        id,
      });
      let parentChange = false;

      if (child && child[0]) {
        const parentRequirementId = path([0, "parentRequirementId"], child);
        const status = path([0, "status"], child);

        if (parentRequirementId === item[i].id) {
          if (!item[i].childs) {
            item[i].childs = 1; // eslint-disable-line  no-param-reassign
          } else if (status === states.initial) {
            item[i].childs += 1; // eslint-disable-line  no-param-reassign
          } else if (allowPush[status]) {
            item[i].childs -= 1; // eslint-disable-line  no-param-reassign
          }
        }

        if (parent && !parent.childs) {
          parent.childs = 0; // eslint-disable-line  no-param-reassign
        }

        if (
          item[i].childs >= item[i].childrenRequired &&
          statusValues[itemStatus] < statusValues[states.submit]
        ) {
          item[i].status = states.check; // eslint-disable-line no-param-reassign, max-len
          parent.childs += 1; // eslint-disable-line  no-param-reassign
          parentChange = true;
        } else if (
          item[i].childs < item[i].childrenRequired &&
          statusValues[itemStatus] > statusValues[states.check]
        ) {
          item[i].status = states.initial; // eslint-disable-line  no-param-reassign, max-len
          parent.childs -= 1; // eslint-disable-line  no-param-reassign
          parentChange = true;
        }

        return parentChange ? [...child, item[i]] : [...child];
      }
    }
  }

  return undefined;
};

const getRequirementUpdates = (
  requirement,
  selectedRequirements,
  dateCompleted,
  leaderUserId = null,
  userCanApprove = false,
  markAsApproved = false,
  overwritePriorDate = true
) => {
  const requirements = [clone(requirement)];
  const requirementsChanges = {};

  forEach((requirementId) => {
    const itemFound = _findRequirementId({
      item: requirements,
      id: requirementId,
    });
    if (itemFound) {
      // update status of requirement with current requirementId
      if (userCanApprove) {
        if (dateCompleted && markAsApproved) {
          itemFound[0].status = states.approved;
        } else if (dateCompleted && !markAsApproved) {
          itemFound[0].status = states.check;
        } else if (!dateCompleted) {
          itemFound[0].status = states.initial;
        }
      } else {
        itemFound[0].status =
          onStatusUpdate[pathOr(states.initial, [0, "status"], itemFound)];
      }

      itemFound.forEach((item) => {
        const {
          id,
          childrenRequired,
          dateCompleted: dateCompletedItem,
          markedCompletedDate: markedCompletedDateItem,
        } = item;

        const isPriorCompletedDate =
          !isNil(dateCompletedItem) && !isEmpty(dateCompletedItem);

        if (item.status === states.initial) {
          requirementsChanges[id] = {
            childrenRequired,
            completed: "False",
            started: "False",
            id,
          };
        } else {
          const formattedCompletedDate = formatDateForAPI(dateCompleted);
          const today = formatDateForAPI(new Date());

          requirementsChanges[id] = {
            dateStarted: formattedCompletedDate,
            dateEarned: formattedCompletedDate,
            childrenRequired,
            completed: "True",
            started: "True",
            id,
          };

          // if overwriting or no prior date, use user-given input
          if (overwritePriorDate || !isPriorCompletedDate) {
            requirementsChanges[id].dateCompleted = formattedCompletedDate;
            requirementsChanges[id].markedCompletedDate =
              formattedCompletedDate !== dateCompletedItem
                ? today
                : markedCompletedDateItem;
          }

          if (userCanApprove) {
            if (markAsApproved) {
              requirementsChanges[id].approved = "True";
              requirementsChanges[id].leaderApprovedDate = today;
              requirementsChanges[id].leaderApprovedUserId = leaderUserId;
            } else {
              requirementsChanges[id].approved = "False";
            }
          }
        }
      });
    }
  }, selectedRequirements);

  return requirementsChanges;
};

export const getRequirementUpdatesFromSets = (
  requirement,
  completeSet,
  approveSet,
  dateCompleted,
  leaderUserId = null,
  userCanApprove = false,
  overwritePriorDate = true
) => {
  const requirements = [clone(requirement)];
  const mergedMarkedRequirements = new Set([...completeSet, ...approveSet]);
  const requirementsChanges = {};

  forEach((requirementId) => {
    const itemFound = _findRequirementId({
      item: requirements,
      id: requirementId,
    });
    if (itemFound) {
      // update status of requirement with current requirementId
      if (userCanApprove) {
        if (approveSet.has(requirementId)) {
          itemFound[0].status = states.approved;
        } else if (dateCompleted && !approveSet.has(requirementId)) {
          itemFound[0].status = states.check;
        } else if (!dateCompleted) {
          itemFound[0].status = states.initial;
        }
      } else {
        itemFound[0].status =
          onStatusUpdate[pathOr(states.initial, [0, "status"], itemFound)];
      }

      itemFound.forEach((item) => {
        const {
          id,
          childrenRequired,
          dateCompleted: dateCompletedItem,
          markedCompletedDate: markedCompletedDateItem,
        } = item;

        const today = formatDateForAPI(new Date());

        const completionDate =
          !isNil(dateCompletedItem) && !isEmpty(dateCompletedItem)
            ? dateCompletedItem
            : formatDateForAPI(dateCompleted);
        const markedCompletedDate =
          !isNil(markedCompletedDateItem) && !isEmpty(markedCompletedDateItem)
            ? markedCompletedDateItem
            : today;

        if (item.status === states.initial) {
          requirementsChanges[id] = {
            childrenRequired,
            completed: "False",
            started: "False",
            id,
          };
        } else {
          requirementsChanges[id] = {
            dateCompleted: completionDate,
            markedCompletedDate,
            dateStarted: completionDate,
            dateEarned: completionDate,
            childrenRequired,
            completed: "True",
            started: "True",
            id,
          };

          if (userCanApprove && approveSet.has(requirementId)) {
            requirementsChanges[id].approved = "True";
            requirementsChanges[id].leaderApprovedDate = today;
            requirementsChanges[id].leaderApprovedUserId = leaderUserId;
          } else {
            requirementsChanges[id].approved = "False";
          }
        }
      });
    }
  }, mergedMarkedRequirements);

  return requirementsChanges;
};

export default getRequirementUpdates;
